import React, { useEffect } from "react";
import { Xmark } from "@gravity-ui/icons";
import "./Sidebar.css";
import { isAdmin } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { useChangeRequestSummary } from "../../hooks/useChangeRequestSummary";
import { useNavigationEntitlements } from "../../hooks/useNavigationEntitlements";
import { filterMenuByNavigation } from "../../utils/navigationEntitlements";
import {
  BASE_MENU_ITEMS,
  ADMIN_MENU_ITEMS,
  ROLE_LABELS,
} from "./sidebarMenu";

const Sidebar = ({
  activeView,
  onNavigate,
  onClose,
  isMobile = false,
  isVisible = false,
}) => {
  const admin = isAdmin();
  const { name: userName, role } = useAuth();
  // ТЗ 27: badge заявок на изменение виден только администратору — клиент
  // /admin/change-requests/summary всё равно получит 403.
  const { pendingCount } = useChangeRequestSummary({ enabled: admin });

  /*
   * NAV-ENTITLEMENTS01 (ТЗ v4 §31, FE-FIX28): состав меню приходит от backend.
   * keys === null — backend без entitlements либо запрос не удался: тогда
   * показываем прежний набор. Пункт меню — это не право доступа, данные всё
   * равно закрывает backend, поэтому «спрятать всё на всякий случай» здесь не
   * защита, а сломанное приложение.
   *
   * Неизвестный ключ игнорируется: из подписи нельзя сделать permission ID.
   * Админская секция остаётся за isAdmin() — навигация администратора по
   * контракту неизменяема.
   */
  const { keys: navigationKeys } = useNavigationEntitlements();
  const baseItems = filterMenuByNavigation(BASE_MENU_ITEMS, navigationKeys);
  const adminItems = admin ? ADMIN_MENU_ITEMS : [];

  // Escape закрывает открытый сайдбар — на десктопе это ожидаемое поведение
  // любого оверлея; на мобильном не мешает.
  useEffect(() => {
    if (!isVisible) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  const handleNavClick = (id) => {
    onNavigate(id);
    if (isMobile) onClose();
  };

  const renderItem = ({ id, label, Icon }) => {
    const active = activeView === id;
    return (
      <button
        key={id}
        type="button"
        className={active ? "nav-btn active" : "nav-btn"}
        aria-current={active ? "page" : undefined}
        onClick={() => handleNavClick(id)}
      >
        <Icon className="nav-icon" width={18} height={18} aria-hidden />
        <span className="nav-label">{label}</span>
        {id === "violations" && pendingCount > 0 && (
          <span className="nav-badge" aria-label="Новые заявки">
            {pendingCount > 99 ? "99+" : pendingCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {isVisible && <div className="sidebar-overlay" onClick={onClose} />}

      {/* inert, а не visibility: hidden — иначе clipPath внутри иконок
          (у @gravity-ui одинаковые id="a") наследует hidden и «выключает»
          такие же иконки на странице, которые ссылаются на первый #a в DOM. */}
      <aside
        className={`sidebar ${!isVisible ? "hidden" : ""}`}
        inert={!isVisible}
        aria-label="Главное меню"
      >
        <div className="sidebar-header">
          <span className="sidebar-brand"><span className="brand-num">24</span>inspector</span>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Закрыть меню"
          >
            <Xmark width={18} height={18} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section">{baseItems.map(renderItem)}</div>

          {adminItems.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Администрирование</div>
              {adminItems.map(renderItem)}
            </div>
          )}
        </nav>

        <div className="sidebar-account">
          <div className="sidebar-account-avatar" aria-hidden>
            {(userName || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div className="sidebar-account-text">
            <div className="sidebar-account-name">{userName}</div>
            <div className="sidebar-account-role">
              {ROLE_LABELS[role] || role}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
